import {
  intelBacktest,
  intelCohortBacktest,
  type CohortBacktest,
  intelOptimize,
  intelPaperBook,
  intelPaperPosition,
  type FacilityBacktest,
  type FacilityOptimize,
  type PaperBook,
  type PaperPositionDetail,
} from '@aithora/shared';
import { useQuery } from '../../useQuery';
import { sx } from '../../ui/designStyles';
import { AboutThis } from '../../ui/AboutThis';
import { EmptyState, ErrorState, Skeleton } from '../../ui/states';
import { A, CAP, CAP9, chip, DASH, KV, onSet, type TabProps } from './common';
import { M, MINUS, pc, type Deal } from './model';
import { ModeBar } from './simulate/ModeBar';
import { BuildLabel } from '../../ui/buildLabels';

const QN = 20;
const TILE = 'box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:9px 2px;';
const FIG = 'font-size:20px;font-weight:700;letter-spacing:-.02em;';
const fmtDay = (iso: string) => new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });

/**
 * Simulate > Track live (V4 aiIsPt, T15866; aiNsVals Paper Trade): a held or planned
 * acquisition marked against the model each quarter. The position, its quarterly marks and
 * Atlas variant are GET /paper-positions/:id; the forward projection is the deal card.
 * Marks say MODELED unless the server says they are realized cost-report figures.
 */
export function PaperTradeTab(p: TabProps) {
  const { f } = p;
  return (
    <>
      <ModeBar p={p} tab="simulate" />
      {f ? <PaperTrade p={p} f={f} /> : <EmptyState title="Nothing to track yet" body="Track a facility from the catalog and follow it here." />}
    </>
  );
}

function PaperTrade({ p, f }: { p: TabProps; f: Deal }) {
  const { st, set, client } = p;
  const mode = st.ptMode || 'Simulate';
  const roi = !!st.ptRoi;
  const fid = f.providerId;

  const book = useQuery<PaperBook>((signal) => intelPaperBook(client, { signal }), [client]);
  const pos = fid ? book.data?.positions.find((x) => x.facilityId === fid && x.status === 'open') ?? null : null;
  const detail = useQuery<PaperPositionDetail>((signal) => intelPaperPosition(client, { params: { id: pos!.id }, signal }), [client, pos?.id], { enabled: !!pos });
  const drift = pos ? detail.data?.drift ?? null : null;

  const comp = drift?.companion ?? null;
  const roiEbitda = roi && comp?.ebitdaWith != null ? comp.ebitdaWith / 1e6 - f.ebitda : 0;
  const evAt = (q: number) => (f.ebitda + roiEbitda) * Math.pow(1.062, q / 4) * f.mult;
  const series = (roi && comp?.series) || drift?.series || [];
  const realized = series.filter((x) => x.realizedEv != null);
  const QLIVE = realized.length;
  const hasHist = !!drift?.hasRealized && QLIVE > 0;

  let maxEV = 0;
  for (let q = 0; q < QN; q++) maxEV = Math.max(maxEV, evAt(q) * 1.05);
  const bars = Array.from({ length: QN }, (_, q) => {
    const mod = evAt(q);
    const real = q < QLIVE ? (realized[q].realizedEv as number) / 1e6 : null;
    return {
      modH: Math.max(4, Math.round((mod / maxEV) * 108)) + 'px',
      realH: real === null ? '0px' : Math.max(4, Math.round((real / maxEV) * 108)) + 'px',
      live: real !== null,
      modBg: real !== null ? 'rgba(47,92,151,.22)' : 'rgba(47,92,151,.5)',
    };
  });

  const entryEV = pos ? pos.entryEv / 1e6 : f.entry;
  const returns = roi && comp ? comp.returns : drift?.current ?? null;
  const markEV = returns ? returns.currentEv / 1e6 : null;
  const yrs = QLIVE ? QLIVE / 4 : null;
  const irr = markEV != null && yrs ? Math.pow(Math.max(markEV / entryEV, 0.01), 1 / yrs) - 1 : null;
  // Realized only when the server marks every quarter REALIZED (cost-report actuals).
  const allRealized = realized.length > 0 && realized.every((x) => x.basis === 'REALIZED');
  const liveNote = hasHist
    ? allRealized
      ? QLIVE + ' quarters realized from HCRIS · the rest is modeled projection'
      : QLIVE + ' quarters marked · MODELED until this facility’s cost reports load · the rest is projection'
    : 'No HCRIS history for a pipeline facility - the whole line is modeled projection';

  const loadingPos = book.loading && !book.data;
  const up = markEV != null && markEV >= entryEV;

  return (
    <div style={sx('display:flex;flex-direction:column;gap:13px;min-width:0;')}>
      <div style={sx('display:flex;align-items:flex-end;gap:14px;flex-wrap:wrap;row-gap:6px;')}>
        <div>
          <div style={sx('font-size:21px;font-weight:700;letter-spacing:-.018em;')}>
            Track live &middot; <span>{f.name}</span>
          </div>
          <div style={sx('font-size:16px;color:#54606C;')}>A held or planned acquisition, marked against the model each quarter.</div>
        </div>
        <span style={sx('flex:1;')} />
        {['Simulate', 'Backtest', 'Optimize'].map((m) => (
          <A key={m} onClick={onSet(set, { ptMode: m })} style={chip(mode === m)}>
            {m === 'Simulate' ? 'Forward' : m}
          </A>
        ))}
        <A onClick={onSet(set, { ptRoi: !roi })} style={chip(roi)}>
          Atlas ROI
        </A>
      </div>

      <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(168px,1fr));gap:11px;')}>
        <div style={sx(TILE)}>
          <div style={sx(CAP + 'margin-bottom:5px;')}>Entry</div>
          <div style={sx(FIG)}>{M(entryEV)}</div>
          <div style={sx('font-size:14px;color:#54606C;')}>{f.status !== 'Acquired' ? 'Not acquired' : pos ? fmtDay(pos.entryDate) : 'no position open'}</div>
        </div>
        <div style={sx(TILE)}>
          <div style={sx(CAP + 'margin-bottom:5px;')}>Value now</div>
          <div style={sx(FIG)}>{markEV == null ? DASH : M(markEV)}</div>
          <div style={sx('font-size:14px;color:#54606C;')}>{yrs == null ? DASH : yrs.toFixed(1) + ' yrs held'}</div>
        </div>
        <div style={sx(TILE)}>
          <div style={sx(CAP + 'margin-bottom:5px;')}>Gain on paper</div>
          <div style={sx(FIG + 'color:' + (markEV == null ? '#22272E' : up ? '#1F6B52' : '#B3261E') + ';')}>{markEV == null ? DASH : (up ? '+' : MINUS) + M(Math.abs(markEV - entryEV))}</div>
          <div style={sx('font-size:14px;color:#54606C;')}>vs entry price</div>
        </div>
        <div style={sx(TILE)}>
          <div style={sx(CAP + 'margin-bottom:5px;')}>IRR to date</div>
          <div style={sx(FIG)}>{irr == null ? DASH : pc(irr)}</div>
          <div style={sx('font-size:14px;color:#54606C;')}>annualized · before debt</div>
        </div>
      </div>

      {loadingPos && <Skeleton rows={4} label="Loading the paper position" />}
      {book.error && !book.data && <ErrorState error={book.error} onRetry={book.reload} />}

      {mode === 'Simulate' && (
        <>
          <div style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:15px 17px;')}>
            <div style={sx('display:flex;align-items:center;gap:12px;flex-wrap:wrap;row-gap:6px;margin-bottom:12px;')}>
              <span style={sx(CAP)}>Facility value &middot; 20 quarters</span>
              <span style={sx('flex:1;')} />
              <span style={sx('display:inline-flex;align-items:center;gap:5px;font-size:14px;color:#54606C;')}>
                <span style={sx('display:inline-block;width:8px;height:8px;border-radius:2px;background:#1F6B52;')} />
                realized
              </span>
              <span style={sx('display:inline-flex;align-items:center;gap:5px;font-size:14px;color:#54606C;')}>
                <span style={sx('display:inline-block;width:8px;height:8px;border-radius:2px;background:rgba(47,92,151,.5);')} />
                modeled
              </span>
            </div>
            <div style={sx('display:flex;align-items:flex-end;gap:4px;height:112px;')}>
              {bars.map((b, i) => (
                <div key={i} style={sx('flex:1;position:relative;height:100%;display:flex;align-items:flex-end;')}>
                  <div style={sx('width:100%;border-radius:2px 2px 0 0;height:' + b.modH + ';background:' + b.modBg + ';')} />
                  {b.live && <div style={sx('position:absolute;left:0;bottom:0;width:100%;border-radius:2px 2px 0 0;height:' + b.realH + ';background:#1F6B52;')} />}
                </div>
              ))}
            </div>
            <div style={sx('font-size:14px;color:#54606C;margin-top:10px;')}>{liveNote}</div>
          </div>

          {realized.length > 0 && (
            <div style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:13px 17px;')}>
              <div style={sx(CAP + 'margin-bottom:9px;')}>Realized vs modeled drift</div>
              <div style={sx('display:grid;grid-template-columns:minmax(0,1.4fr) minmax(0,1fr) minmax(0,1fr) minmax(0,1fr);gap:10px;' + CAP9 + 'padding-bottom:8px;border-bottom:1px solid rgba(34,39,46,.1);')}>
                <span>Quarter</span>
                <span style={sx('text-align:right;')}>Realized EV</span>
                <span style={sx('text-align:right;')}>Modeled EV</span>
                <span style={sx('text-align:right;')}>Drifting</span>
              </div>
              {realized.map((d) => {
                const real = (d.realizedEv as number) / 1e6;
                const mod = d.modeledEv / 1e6;
                const pos2 = real >= mod;
                return (
                  <div key={d.date} style={sx('display:grid;grid-template-columns:minmax(0,1.4fr) minmax(0,1fr) minmax(0,1fr) minmax(0,1fr);gap:10px;font-size:16px;padding:9px 0;border-bottom:1px solid rgba(34,39,46,.06);font-variant-numeric:tabular-nums;')}>
                    <span style={sx('color:#3C454F;')}>{d.label}</span>
                    <span style={sx('text-align:right;')}>{M(real)}</span>
                    <span style={sx('text-align:right;color:#54606C;')}>{M(mod)}</span>
                    <span style={sx('text-align:right;font-weight:700;color:' + (pos2 ? '#1F6B52' : '#B3261E') + ';')}>{(pos2 ? '+' : MINUS) + M(Math.abs(real - mod))}</span>
                  </div>
                );
              })}
            </div>
          )}

          {roi && (
            <div style={sx('box-sizing:border-box;background:rgba(47,138,111,.07);border:1px solid rgba(47,138,111,.3);border-radius:11px;padding:15px 17px;')}>
              <div style={sx('font-size:14px;letter-spacing:.11em;text-transform:uppercase;color:#1F6B52;font-weight:600;margin-bottom:11px;')}>Atlas ROI folded into the position</div>
              {(
                [
                  ['Reimbursement capture', comp?.reimbursementPerYear == null ? DASH : '+' + M(comp.reimbursementPerYear / 1e6), 'Part-B + MA benefits, per bed / yr'],
                  ['EBITDA with Aithora', comp?.ebitdaWith == null ? DASH : M(comp.ebitdaWith / 1e6), comp?.ebitdaUpliftPct == null ? DASH : '+' + pc(comp.ebitdaUpliftPct, 1) + ' vs as-is'],
                  ['IRR from Aithora', comp ? '+' + comp.irrUpliftPts.toFixed(1) + ' pts' : DASH, 'folded into the position'],
                ] as [string, string, string][]
              ).map(([label, val, note]) => (
                <div key={label} style={sx('display:flex;align-items:baseline;gap:11px;flex-wrap:wrap;row-gap:6px;font-size:16px;padding:9px 0;border-bottom:1px solid rgba(47,138,111,.16);')}>
                  <span style={sx('color:#3C454F;')}>{label}</span>
                  <span style={sx('font-size:14px;color:#54606C;')}>{note}</span>
                  <span style={sx('flex:1;')} />
                  <span style={sx('font-variant-numeric:tabular-nums;font-weight:700;color:#1F6B52;')}>{val}</span>
                </div>
              ))}
              {/* V9: the note opens on the "i" (design k17372). */}
              <AboutThis inline textStyle="font-size:14px;color:#54606C;margin-top:9px;">
                Uplift is modeled from our own reimbursement-capture and staffing figures, not this facility&apos;s realized result.
              </AboutThis>
            </div>
          )}
        </>
      )}

      {mode === 'Backtest' && <Backtest p={p} f={f} liveNote={liveNote} />}
      {mode === 'Optimize' && <Optimize p={p} f={f} />}
    </div>
  );
}

function Backtest({ p, f, liveNote }: { p: TabProps; f: Deal; liveNote: string }) {
  const fid = f.providerId;
  const bt = useQuery<FacilityBacktest>((signal) => intelBacktest(p.client, { params: { id: fid! }, signal }), [p.client, fid], { enabled: !!fid });
  const s = bt.data?.summary ?? null;
  const rows: [string, string][] = s
    ? [
        ['Realized entry EV', M(s.entryEv / 1e6)],
        ['Realized exit EV', M(s.exitEv / 1e6)],
        ['Realized IRR', pc(s.irr)],
        ['Realized MOIC', s.moic.toFixed(2) + 'x'],
        ['Modeled IRR at entry', pc(s.modeledIrr)],
        ['Model error', s.modelErrorPts.toFixed(1) + ' pts'],
      ]
    : [];
  return (
    <div style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:15px 17px;')}>
      <div style={sx('display:flex;align-items:center;gap:9px;flex-wrap:wrap;row-gap:6px;margin-bottom:11px;')}>
        <span style={sx(CAP)}>Realized backtest</span>
        <span style={sx('flex:1;')} />
        {s &&
          (s.basis ? (
            <BuildLabel><span style={sx('font-variant-numeric:tabular-nums;font-size:14px;font-weight:700;letter-spacing:.09em;color:#1F6B52;background:rgba(47,138,111,.13);border-radius:4px;padding:2px 6px;')}>LIVE &middot; HCRIS</span></BuildLabel>
          ) : (
            <BuildLabel><span style={sx('font-variant-numeric:tabular-nums;font-size:14px;font-weight:700;letter-spacing:.09em;color:#54606C;background:rgba(34,39,46,.12);border-radius:4px;padding:2px 6px;')}>MODELED &middot; HCRIS feed pending</span></BuildLabel>
          ))}
      </div>
      {bt.loading && !bt.data && <Skeleton rows={4} label="Running the backtest" />}
      {bt.error && !bt.data && <ErrorState error={bt.error} onRetry={bt.reload} />}
      {rows.map(([label, val]) => (
        <KV key={label} label={label} val={val} />
      ))}
      <div style={sx('font-size:14px;color:#54606C;margin-top:11px;')}>{liveNote}</div>
      {/* Restored from production (simulate_panel.dart): the state's turnaround cohort. */}
      <CohortLine p={p} state={f.stt} />
    </div>
  );
}

export function cohortText(c: CohortBacktest, state: string): string {
  if (!c.available || !c.cohort) return c.reason ?? 'Not enough comparable cost reports for a cohort.';
  const pts = (x: number) => (x >= 0 ? '+' : '') + (x * 100).toFixed(1) + ' pts';
  return `${state} turnaround cohort: ${c.cohortN} of ${c.n} SNFs raised margin (${pts(c.cohort.avgMarginDelta)}); ${Math.round(c.cohort.pctImproved * 100)}% of them also raised occupancy.`;
}

function CohortLine({ p, state }: { p: TabProps; state: string }) {
  const q = useQuery<CohortBacktest>((signal) => intelCohortBacktest(p.client, { query: state ? { state } : {}, signal }), [p.client, state]);
  if (!q.data) return null;
  return <div data-testid="intel-cohort" style={sx('font-size:14px;color:#3C454F;margin-top:6px;line-height:1.45;')}>{cohortText(q.data, state || 'National')}</div>;
}

function Optimize({ p, f }: { p: TabProps; f: Deal }) {
  const fid = f.providerId;
  const opt = useQuery<FacilityOptimize>((signal) => intelOptimize(p.client, { params: { id: fid! }, body: { objective: 'irr', combos: 3 }, signal }), [p.client, fid], { enabled: !!fid });
  const combos = opt.data?.combos ?? [];
  return (
    <div style={sx('box-sizing:border-box;border-top:1px solid rgba(34,39,46,.14);padding:15px 17px;')}>
      <div style={sx(CAP + 'margin-bottom:12px;')}>Best lever combinations</div>
      {opt.loading && !opt.data && <Skeleton rows={3} label="Searching lever combinations" />}
      {opt.error && !opt.data && <ErrorState error={opt.error} onRetry={opt.reload} />}
      {combos.map((o) => (
        <div key={o.label} style={sx('padding:10px 0;border-bottom:1px solid rgba(34,39,46,.06);')}>
          <div style={sx('display:flex;align-items:baseline;gap:10px;flex-wrap:wrap;row-gap:6px;font-size:16px;margin-bottom:6px;')}>
            <span style={sx('font-weight:600;')}>{o.label}</span>
            <span style={sx('flex:1;')} />
            <span style={sx('font-variant-numeric:tabular-nums;font-weight:700;color:#1F6B52;')}>{'+' + M(o.evUpliftPct * f.entry)}</span>
            <span style={sx('font-variant-numeric:tabular-nums;font-size:14px;color:#54606C;')}>{'+' + o.irrBps + ' bps'}</span>
          </div>
          <div style={sx('height:6px;border-radius:3px;background:rgba(34,39,46,.07);')}>
            <div style={sx('height:6px;border-radius:3px;background:#1F6B52;width:' + ((o.evUpliftPct / 0.26) * 100).toFixed(0) + '%;')} />
          </div>
        </div>
      ))}
      <div style={sx('font-size:14px;color:#54606C;margin-top:10px;')}>Combinations are gated by risk appetite on the Scenario tab - nothing above your appetite is proposed here.</div>
    </div>
  );
}
